<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron @click="$router.push({name: 'Workspace'})"/>
                Biomarcadores pendientes (fallo de unidad)
              </h1>
            </div>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="notFoundTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">

              <table-column show="id" :hidden="true" :filterable="false" data-type="boolean"></table-column>

              <table-column show="name" label="Nombre extraído" header-class="text-left" cell-class="table-component__text-left"></table-column>
              <table-column show="unit" label="Unidad extraída" header-class="text-left" cell-class="table-component__text-left"></table-column>
              <table-column show="value" label="Valor" header-class="text-left" cell-class="table-component__text-left"></table-column>

              <table-column
                label="Unidad esperada"
                :sortable="false"
                :filterable="false"
                header-class="text-left"
                cell-class="table-component__text-left">
                <template slot-scope="row">
                  <strong>{{ row.expectedUnit }}</strong>
                </template>
              </table-column>

              <table-column
                label="Factor"
                :sortable="false"
                :filterable="false"
                header-class="text-left"
                cell-class="table-component__text-left">
                <template slot-scope="row">
                  <div style="display: flex; align-items: center; gap: 5px;">
                    <span style="color: #666; font-weight: bold;">x</span>
                    <input
                      type="number"
                      step="any"
                      v-model.number="conversionFactors[row.id]"
                      class="factor-input"
                      title="Multiplicador para convertir la unidad extraída a la esperada">
                  </div>
                </template>
              </table-column>

              <table-column
                label="Resolver"
                :sortable="false"
                :filterable="false"
                header-class="text-left"
                cell-class="table-component__text-left">
                <template slot-scope="row">
                  <div style="display: flex; gap: 10px; align-items: center;">
                    <button
                      class="btn btn-success btn-sm"
                      :disabled="!row.expectedUnit"
                      @click="onAccept(row)">
                      Aceptar
                    </button>

                    <button
                      class="btn btn-danger btn-sm"
                      @click="onReject(row)">
                      Rechazar
                    </button>
                  </div>
                </template>
              </table-column>

            </table-component>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import Chevron from '@/components/shared/Chevron';
import AnalyticsService from 'BRAND/api/analytics';

export default {
  name: 'NotFoundBiomarkersUnitList',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    Chevron
  },
  data () {
    return {
      notFoundBiomarkers: [],
      conversionFactors: {}, // Diccionario reactivo para los multiplicadores
      breadcrumb: [
        {link: '/workspace', name: 'Workspace'},
        {link: '/analytics/not-found/units', name: 'Unidades no encontradas', disabled: true}
      ],
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: { order: '', fieldName: '' }
      }
    };
  },
  created () {
    this.requestNotFoundBiomarkers();
  },
  computed: {
    getBreadcrumb () {
      return this.breadcrumb;
    }
  },
  methods: {
    fetchData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.notFoundBiomarkers,
        this.tableState,
        ['name', 'unit', 'value'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },

    async requestNotFoundBiomarkers () {
      try {
        const rawData = await AnalyticsService.getNotFoundUnits(this);
        const dataArray = Array.isArray(rawData) ? rawData : (rawData.data || []);

        this.notFoundBiomarkers = dataArray.map(item => {
          // Para UNIT, expectedUnit viene del biomarcador oficial asociado; mantenemos fallback por compatibilidad.
          const expectedUnit = item.expectedUnit || ((item.candidates && item.candidates.length > 0) ? item.candidates[0].unit : '');

          // Inicializamos el factor de conversión en 1 por defecto
          this.$set(this.conversionFactors, item.id, 1);

          return {
            ...item,
            expectedUnit: expectedUnit,
            value: item.value != null ? String(item.value) : ''
          };
        });

        this.refreshTable();
      } catch (error) {
        console.error("Error al cargar las unidades pendientes:", error);
      }
    },

    refreshTable () {
      this.$nextTick(() => {
        if (this.$refs.notFoundTable) {
          this.$refs.notFoundTable.refresh();
        }
      });
    },

    async onAccept (row) {
      const factor = this.conversionFactors[row.id];

      // Validación simple para asegurar que no han borrado el número
      if (factor === null || factor === undefined || factor === '') {
        alert('Por favor, indica un factor de conversión válido (usa 1 si no requiere conversión).');
        return;
      }

      try {
        // Le pasamos el factor a la API (el backend podrá ignorarlo por ahora hasta que lo implementes)
        await AnalyticsService.resolveUnit(this, row.id, row.expectedUnit, factor);

        this.notFoundBiomarkers = this.notFoundBiomarkers.filter(item => item.id !== row.id);
        this.$delete(this.conversionFactors, row.id);
        this.refreshTable();
      } catch (error) {
        console.error('Error al resolver unidad:', error);
        alert("No se pudo guardar la resolución.");
      }
    },

    async onReject (row) {
      if (!confirm(`¿Estás seguro de que quieres rechazar la unidad '${row.unit}' de '${row.name}'?`)) return;

      try {
        await AnalyticsService.rejectBiomarker(this, row.id);

        this.notFoundBiomarkers = this.notFoundBiomarkers.filter(item => item.id !== row.id);
        this.$delete(this.conversionFactors, row.id);
        this.refreshTable();
      } catch (error) {
        console.error('Error al rechazar unidad:', error);
        alert('No se pudo rechazar la unidad.');
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "src/stylesheets/variables";

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
}

.header-title {
  float: left;
}

.factor-input {
  width: 70px;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  text-align: center;
}
/* Quitar flechas del input number para que quede más limpio */
.factor-input::-webkit-outer-spin-button,
.factor-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/analytics/NotFoundBiomarkersUnitList.vue